import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Controller, useController, useFormContext } from "react-hook-form";

import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";

import AvatarCrop from "components/avatar-crop";
import type { AvatarCropHandle } from "components/avatar-crop";
import ImageCrop from "components/image-crop";
import { formatBytes } from "utils/common";

const CustomBox = styled(Box)(({ theme }) => ({
  "&.MuiBox-root": {
    backgroundColor: theme.palette.common.white,
    borderRadius: "12px",
    boxShadow: `${theme.palette.common.black}19 0px 8px 24px`,
    padding: "8px"
  },
  "&.MuiBox-root:hover, &.MuiBox-root.dragover": {
    opacity: 0.6
  }
}));

function FilePreview({ file }: { file: File }) {
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  return (
    <img
      style={{ maxHeight: "80px", maxWidth: "100%", objectFit: "contain" }}
      src={previewUrl}
      alt="Preview"
    />
  );
}

interface ImageUploadProps {
  name: string;
  onRemove?: () => void;
  hasInitialImage?: boolean;
  currentImageSrc?: string;
  cropImage?: boolean;
  setCropImage?: (cropImage: boolean) => void;
  onChange?: (files: File[]) => void;
  /**
   * Drops the upload dropzone while keeping everything else — the selected
   * file rows and the "Current Image" row with its delete action. For a
   * surface whose files arrive programmatically (the signature desks write
   * into the field via setValue), where offering an upload is unwanted but
   * removing/reviewing must keep working (SN-19567).
   */
  hideUploadInput?: boolean;
  /**
   * Opt in to square, circular avatar cropping. Entirely separate from the
   * `cropImage` flag above, which is SignaturePicker's button-driven
   * free-form flow through `components/image-crop`.
   */
  avatarCrop?: boolean;
  cropRef?: React.Ref<AvatarCropHandle>;
  /** Name given to the file ImageCrop produces (SN-18765). */
  croppedFileName?: string;
  /**
   * Swaps the staged-file row's 24px thumbnail for an 80px bordered preview
   * (SN-18765). Opt-in, not a default: the logo and signature desks are
   * reviewing an image before saving it, where a thumbnail is useless, while
   * insurance-card and product-photo uploads are just confirming a filename
   * and must keep the compact row.
   */
  largePreview?: boolean;
  /**
   * Alt text for the "Current Image" preview below. Defaults to a generic
   * "Current image" since this component is now shared with the logo
   * dialogs — SignaturePicker overrides it back to "Current signature".
   */
  currentImageAlt?: string;
}

export default function ImageUpload({
  name,
  onRemove,
  hasInitialImage,
  currentImageSrc,
  cropImage,
  setCropImage,
  onChange,
  hideUploadInput,
  avatarCrop,
  cropRef,
  croppedFileName,
  largePreview,
  currentImageAlt = "Current image"
}: ImageUploadProps) {
  const {
    control,
    formState: { errors }
  } = useFormContext();
  const theme = useTheme();
  const { field } = useController({ name, control });

  // The cropper has to read from the ORIGINAL file, never from the field it
  // writes to. Feeding it back its own output would let each adjustment crop
  // the previous crop, losing pixels a drag at a time.
  const [cropSource, setCropSource] = useState<File | null>(null);

  const [imgError, setImgError] = useState(false);
  const [cropCurrentImage, setCropCurrentImage] = useState(false);
  const [cropLoadError, setCropLoadError] = useState<string | null>(null);

  useEffect(() => {
    setImgError(false);
    setCropCurrentImage(false);
    setCropLoadError(null);
  }, [currentImageSrc]);

  const handleCropLoadError = useCallback((message: string) => {
    setCropLoadError(message);
    setCropCurrentImage(false);
  }, []);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");

  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");
  const onFileDrop = useCallback(
    (e: React.SyntheticEvent<EventTarget>) => {
      const target = e.target as HTMLInputElement;
      if (!target.files) return;

      const newFiles = Object.values(target.files).map((file: File) => file);
      field.onChange(newFiles);
      if (avatarCrop) {
        setCropSource(newFiles[0] ?? null);
      }

      onChange?.(newFiles);

      !cropImage && setCropImage && setCropImage(true);
    },

    [field, onChange, cropImage, setCropImage, avatarCrop]
  );

  // Remembers the file the cropper last produced, so the effect below can tell
  // "the cropper wrote its own output" apart from "something replaced the
  // image". Without it the cropper would re-read its own crop and crop that.
  //
  // The field write matters beyond the submit path: PhotoPicker's size gate
  // watches this value, so it has to measure the cropped file rather than the
  // original the user picked.
  const croppedOutputRef = useRef<File | null>(null);
  const handleCropped = useCallback(
    (file: File) => {
      croppedOutputRef.current = file;
      field.onChange([file]);
    },
    [field]
  );

  // Webcam capture, clipboard paste and document scanning all push straight
  // into the form with setValue, bypassing onFileDrop — pick those up too, and
  // pick them up even when a photo is already loaded, so switching to the
  // webcam mid-crop doesn't leave the cropper on the previous image.
  const fieldValue = field.value;
  useEffect(() => {
    if (!avatarCrop) return;

    const current = Array.isArray(fieldValue) ? fieldValue[0] : undefined;
    if (!current) {
      setCropSource(null);
      croppedOutputRef.current = null;
      return;
    }

    if (!(current instanceof File)) return;

    if (current !== cropSource && current !== croppedOutputRef.current) {
      setCropSource(current);
      croppedOutputRef.current = null;
    }
  }, [avatarCrop, cropSource, fieldValue]);

  const removeFile = useCallback(() => {
    field.onChange([]);
    setCropSource(null);
    if (onRemove) {
      onRemove();
    }
  }, [field, onRemove]);

  // Distinct from removeFile: that one fires onRemove, which for PhotoPicker
  // DELETES the saved photo and closes the dialog. Backing out of a file you
  // just picked must not do that — it only drops the pending selection. The
  // "delete the existing image" affordance stays on the hasInitialImage row
  // below, which is what shows when nothing is selected.
  const clearSelection = useCallback(() => {
    field.onChange([]);
    setCropSource(null);

    // The button invites the user to pick again, and a file input does not
    // fire `change` when re-selecting the path it already holds — so without
    // this, choosing the same photo a second time is a no-op.
    const input = wrapperRef.current?.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement | null;
    if (input) {
      input.value = "";
    }
  }, [field]);

  return (
    <>
      {!hideUploadInput && (
        <CustomBox>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: "relative",
              minWidth: "300px",
              width: "100%",
              height: "13rem",
              border: `1px dashed ${theme.palette.secondary.main}`,
              borderRadius: "12px"
            }}
            ref={wrapperRef}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDragLeave}
          >
            <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
              <div>
                <FileDownloadIcon />
              </div>
              <Typography variant="body1" component="span">
                Upload a Photo
              </Typography>
            </Stack>
            <Controller
              name={name}
              defaultValue=""
              control={control}
              render={({ field: { name, onBlur, ref } }) => (
                <input
                  type="file"
                  name={name}
                  onBlur={onBlur}
                  ref={ref}
                  onChange={onFileDrop}
                  accept="image/jpg, image/png, image/jpeg"
                  style={{
                    opacity: 0,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    cursor: "pointer"
                  }}
                />
              )}
            />
          </Box>
        </CustomBox>
      )}
      <FormHelperText sx={{ textAlign: "center", my: 1 }}>
        <>{errors && errors[name] ? errors[name]?.message : ""}</>
      </FormHelperText>

      <Stack spacing={2} sx={{ my: 2, minWidth: "300px" }}>
        {avatarCrop && cropSource && (
          <Box
            sx={{
              position: "relative",
              backgroundColor: `${theme.palette.info.light}10`,
              borderRadius: 1.5,
              px: 0.5,
              py: 1
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, textAlign: "center" }}>
              Drag to reposition, scroll or pinch to zoom.
            </Typography>
            <AvatarCrop
              ref={cropRef}
              imageSrc={cropSource}
              onCropped={handleCropped}
            />
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={clearSelection}
              sx={{ color: theme.palette.error.main, mt: 1 }}
            >
              Choose a different photo
            </Button>
          </Box>
        )}

        {!avatarCrop &&
          field?.value?.map((item: any, index: number) => {
            return (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  backgroundColor: `${theme.palette.info.light}10`,
                  borderRadius: 1.5,
                  px: 0.5,
                  py: 1
                }}
              >
                {cropImage ? (
                  <ImageCrop
                    setCropImage={setCropImage}
                    imageSrc={item}
                    field={field}
                    croppedFileName={croppedFileName}
                  />
                ) : largePreview ? (
                  // The logo and signature desks are reviewing the image
                  // itself before saving it, so they get a real preview
                  // rather than the filename-confirmation row below.
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mb: 1,
                        border: "1px dashed lightgray",
                        borderRadius: 1,
                        p: 1
                      }}
                    >
                      <FilePreview file={item} />
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Box sx={{ flex: 1, ml: 0.5 }}>
                        <Typography>{item.name}</Typography>
                        <Typography variant="body2">
                          {formatBytes(item.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        // Same rule as the compact row below — see the comment
                        // there. Discarding a desk capture must not delete the
                        // SAVED image server-side.
                        onClick={hideUploadInput ? clearSelection : removeFile}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box display="flex">
                      <Box display="flex" sx={{ alignItems: "center" }}>
                        <img
                          style={{ height: "24px", width: "24px" }}
                          src={URL.createObjectURL(item)}
                          alt="Preview"
                        />
                      </Box>

                      <Box sx={{ ml: 1, maxWidth: "calc(100% - 50px)" }}>
                        <Typography sx={{ mb: 1 }}>{item.name}</Typography>
                        <Typography variant="body2">
                          {formatBytes(item.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      // With the dropzone hidden the staged file is a desk
                      // capture under review: its trash discards the capture
                      // (back to the Current Image row), while removeFile's
                      // onRemove would delete the SAVED image server-side and
                      // close the dialog. The upload flow keeps removeFile.
                      onClick={hideUploadInput ? clearSelection : removeFile}
                      sx={{
                        color: theme.palette.error.main,
                        position: "absolute",
                        right: "0.25rem",
                        top: "50%",
                        transform: "translateY(-50%)"
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            );
          })}

        {hasInitialImage && !Boolean(field?.value?.length) && (
          <Box
            sx={{
              backgroundColor: `${theme.palette.info.light}10`,
              borderRadius: 1.5,
              px: 1,
              py: 1
            }}
          >
            {cropCurrentImage && currentImageSrc ? (
              <ImageCrop
                imageSrc={currentImageSrc}
                field={field}
                setCropImage={setCropCurrentImage}
                croppedFileName={croppedFileName}
                onLoadError={handleCropLoadError}
              />
            ) : (
              <>
                {cropLoadError && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {cropLoadError}
                  </Alert>
                )}
                {currentImageSrc && !imgError && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mb: 1,
                      border: "1px dashed lightgray",
                      borderRadius: 1,
                      p: 1
                    }}
                  >
                    <img
                      style={{
                        maxHeight: "80px",
                        maxWidth: "100%",
                        objectFit: "contain"
                      }}
                      src={currentImageSrc}
                      alt={currentImageAlt}
                      onError={() => setImgError(true)}
                    />
                  </Box>
                )}
                <Box display="flex" alignItems="center">
                  <Box sx={{ flex: 1, ml: 0.5 }}>
                    <Typography>Current Image</Typography>
                  </Box>
                  {currentImageSrc && !imgError && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setCropCurrentImage(true)}
                    >
                      Crop
                    </Button>
                  )}
                  <IconButton
                    onClick={removeFile}
                    sx={{ color: theme.palette.error.main }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
        )}
      </Stack>
    </>
  );
}
